import React, { useState, useMemo } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { formatDateTime } from "../../../common/constants/dashboardModule/optinConstants.js";
import { fetchHiResponses } from "../../../services/dashboardModule/dashboardServices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";

// ── Constants ──────────────────────────────────────────────────────
const BLUE  = "#378ADD";
const AMBER = "#BA7517";

// ── Helpers ────────────────────────────────────────────────────────
function parseLocalDate(val) {
    if (!val) return null;
    const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

const formatDate = (val) => {
    const d = parseLocalDate(val);
    if (!d || Number.isNaN(d.getTime())) return val || "—";
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

const formatDateTimeDisplay = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleString("en-US", {
        month: "2-digit", day: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
    });
};

const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function SectionTitle({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
        }}>
            {children}
        </Typography>
    );
}

// ── Component ──────────────────────────────────────────────────────
const DailyAccountIds = () => {
    const [date,     setDate]     = useState(() => new Date());
    const [rows,     setRows]     = useState([]);
    const [searched, setSearched] = useState(false);
    const [loading,  setLoading]  = useState(false);
    const [error,    setError]    = useState("");
    const [sortDir,  setSortDir]  = useState("asc");

    // The list is ordered by Sent Time (primary), then Account ID (tiebreaker),
    // matching the backend ORDER BY cararesponsetime ASC, customerid ASC.
    const SORT_KEY = "ResponseTime";

    // ── Column config ─────────────────────────────────────────────
    const COLS = useMemo(() => [
        { key: "CustomerId",   label: "Account ID", width: 200, align: "left" },
        { key: "Type",         label: "Type",       width: 160, align: "left" },
        { key: "ResponseTime", label: "Sent Time",  width: 200, align: "left" },
    ], []);

    const GRID_COLS   = COLS.map(c => `${c.width}px`).join(" ");
    const TABLE_WIDTH = COLS.reduce((s, c) => s + c.width, 0) + 64;

    const load = async (d) => {
        try {
            setLoading(true); setError(""); setSearched(true);
            const data = await fetchHiResponses({ date: formatDateTime(d) });
            setRows(data || []);
        } catch (e) {
            console.error("Failed to load daily account IDs", e);
            setError("Failed to load account IDs.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const sortedRows = useMemo(() => {
        const sorted = [...rows].sort((a, b) => {
            // Primary: Sent Time (datetime). Secondary: Account ID (numeric).
            const ta = new Date(a.ResponseTime).getTime();
            const tb = new Date(b.ResponseTime).getTime();
            let cmp = (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
            if (cmp === 0) {
                cmp = String(a.CustomerId ?? "").localeCompare(
                    String(b.CustomerId ?? ""), undefined, { numeric: true }
                );
            }
            return sortDir === "asc" ? cmp : -cmp;
        });
        return sorted;
    }, [rows, sortDir]);

    // ── CSV export ────────────────────────────────────────────────
    const exportCsv = () => {
        if (!sortedRows.length) return;

        const headers = COLS.map(c => csvEscape(c.label));
        const body = sortedRows.map(row =>
            COLS.map(col => {
                if (col.key === "ResponseTime") return csvEscape(formatDateTimeDisplay(row.ResponseTime));
                if (col.key === "Type")         return csvEscape(row.Type || "Initial Message");
                return csvEscape(row[col.key]);
            }).join(",")
        );
        const csv  = "\uFEFF" + [headers.join(","), ...body].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url  = URL.createObjectURL(blob);

        const d  = parseLocalDate(date) || new Date();
        const ds = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;

        const a = document.createElement("a");
        a.href     = url;
        a.download = `daily_accountids_${ds}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const gridSx = {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        width: `${TABLE_WIDTH}px`,
        px: 2,
    };

    const exportDisabled = loading || sortedRows.length === 0;

    return (
        <Box sx={{ p: 0 }}>
            {/* ── Filters ── */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 2,
                alignItems: "flex-end",
                pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
            }}>
                <DatePickerComponent
                    label={STRINGS.DATE || "Date"}
                    date={date}
                    onDateChange={setDate}
                    marginRight={0}
                />

                <Box onClick={() => load(date)} sx={{
                    px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                    cursor: "pointer", userSelect: "none",
                    background: BLUE, color: "#fff", fontWeight: 500,
                    alignSelf: "flex-end", mb: "1px",
                    "&:hover": { background: "#2a6db5" },
                }}>Search</Box>

                <Box
                    onClick={exportDisabled ? undefined : exportCsv}
                    title={exportDisabled ? "No data to export" : "Export account IDs to CSV"}
                    sx={{
                        px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                        userSelect: "none",
                        border: "0.5px solid",
                        borderColor: exportDisabled ? "divider" : BLUE,
                        background: "transparent",
                        color: exportDisabled ? "text.disabled" : BLUE,
                        fontWeight: 500,
                        cursor: exportDisabled ? "default" : "pointer",
                        opacity: exportDisabled ? 0.5 : 1,
                        alignSelf: "flex-end", mb: "1px",
                        display: "inline-flex", alignItems: "center", gap: "6px",
                        transition: "all .15s",
                        "&:hover": exportDisabled ? {} : { background: "rgba(55,138,221,0.06)" },
                    }}
                >
                    <span style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
                    Export CSV
                </Box>
            </Box>

            {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
                    <CircularProgress size={20} /> Loading…
                </Box>
            )}

            {!loading && searched && rows.length === 0 && !error && (
                <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
                    No account IDs for {formatDate(date)}.
                </Typography>
            )}

            {!loading && rows.length > 0 && (
                <>
                    <SectionTitle>
                        {rows.length.toLocaleString()} account{rows.length === 1 ? "" : "s"} · {formatDate(date)}
                    </SectionTitle>

                    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto" }}>
                        {/* Header */}
                        <Box sx={{
                            ...gridSx, py: 1,
                            background: "#f7f7f7",
                            borderBottom: "0.5px solid", borderColor: "divider",
                            fontSize: 11, fontWeight: 500,
                            textTransform: "uppercase", letterSpacing: "0.05em",
                            alignItems: "center",
                        }}>
                            {COLS.map(col => {
                                const isSortCol = col.key === SORT_KEY;
                                return (
                                    <Box
                                        key={col.key}
                                        onClick={isSortCol
                                            ? () => setSortDir(d => d === "asc" ? "desc" : "asc")
                                            : undefined}
                                        sx={{
                                            textAlign: col.align,
                                            cursor: isSortCol ? "pointer" : "default",
                                            userSelect: "none",
                                            display: "flex", gap: 0.5, alignItems: "center",
                                            color: isSortCol ? BLUE : "text.secondary",
                                            "&:hover": isSortCol ? { color: BLUE } : {},
                                        }}
                                    >
                                        {col.label}
                                        {isSortCol && (
                                            <span style={{ fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Rows */}
                        {sortedRows.map((row, i) => {
                            const isReminder = row.Type === "Reminder";
                            return (
                                <Box key={`${row.CustomerId}_${i}`} sx={{
                                    ...gridSx, py: "7px", fontSize: 12,
                                    borderBottom: "0.5px solid", borderColor: "divider",
                                    background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                                    "&:hover": { background: "rgba(55,138,221,0.06)" },
                                    alignItems: "center",
                                }}>
                                    <Box sx={{ fontFamily: "monospace", fontWeight: 500 }}>
                                        {row.CustomerId ?? "—"}
                                    </Box>
                                    <Box>
                                        <Box component="span" sx={{
                                            display: "inline-block",
                                            px: "8px", py: "2px", borderRadius: "20px",
                                            fontSize: 11, fontWeight: 500,
                                            color:      isReminder ? AMBER : BLUE,
                                            background:  isReminder ? "rgba(186,117,23,0.10)" : "rgba(55,138,221,0.10)",
                                        }}>
                                            {row.Type || "Initial Message"}
                                        </Box>
                                    </Box>
                                    <Box sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                                        {formatDateTimeDisplay(row.ResponseTime)}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default DailyAccountIds;